import { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TbFilter } from "react-icons/tb";
import { Context } from "../Context";
import SeasonDropdownMenu from "./SeasonDropdownMenu";

const throttle = (func, limit) => {
  let lastFunc;
  let lastRan;

  return (...args) => {
    const context = this;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
};

const FishDisplay = () => {
  const context = useContext(Context);
  const accessToken = context.accessToken;
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const serverId = useParams().serverId;
  const currentYear = new Date().getFullYear();
  const currentSeason = currentYear - 2024 + 1;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [season, setSeason] = useState(currentSeason);
  const [catchesData, setCatchesData] = useState([]);
  const [filterChecked, setFilterChecked] = useState(true);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      // Use window.scrollY instead of documentElement.scrollTop for better mobile support
      const scrollPosition = window.scrollY || window.pageYOffset;
      const viewportHeight = window.innerHeight;
      const totalHeight = document.documentElement.scrollHeight;

      // Check if we're near the bottom (80% of viewport height from bottom)
      if (
        scrollPosition + viewportHeight >= totalHeight - viewportHeight * 0.8 &&
        !isLoading &&
        currentPage < totalPages
      ) {
        setCurrentPage((prev) => prev + 1);
      }
    };

    // Throttle the scroll handler (300ms delay)
    const throttledHandleScroll = throttle(handleScroll, 300);
    window.addEventListener("scroll", throttledHandleScroll, { passive: true }); // Add event listener
    return () => window.removeEventListener("scroll", throttledHandleScroll); // Cleanup
  }, [isLoading, currentPage, totalPages]);

  const getCatchesData = useCallback(async () => {
    if (!accessToken) return; // Wait until tokens are available

    try {
      const response = await fetch(
        `${apiBaseUrl}/api/servers/${serverId}/fish?season=${season}&page=${currentPage}&limit=15&newest=${filterChecked}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`, // Supabase JWT in header
          },
        }
      );

      if (response.status === 404) navigate("/404");
      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error("Failed to fetch fish data", error);
    }
  }, [apiBaseUrl, serverId, season, filterChecked, navigate, currentPage, accessToken]);

  useEffect(() => {
    const getCatches = async () => {
      setIsLoading(true);
      const data = await getCatchesData();

      if (!data?.fishData) {
        setCatchesData([]);
      } else {
        // Append new data or replace if first page
        if (currentPage === 1) {
          setCatchesData(data.fishData);
        } else {
          setCatchesData((prev) => [...prev, ...data.fishData]);
        }
        setTotalPages(data.totalPages);
      }
      setIsLoading(false);
    };

    getCatches();
  }, [getCatchesData, currentPage]);

  const updateSeason = (newSeason) => {
    setFilterChecked(true);
    setCurrentPage(1);
    setSeason(newSeason);
    setCatchesData([]);
  };

  const updateFilter = () => {
    setFilterChecked(!filterChecked);
    setCurrentPage(1);
    setCatchesData([]);
  };

  return (
    <>
      <SeasonDropdownMenu onChange={updateSeason} />
      <button
        className="absolute inline-flex items-center bg-[#e6e6e0] py-1 px-2 hover:bg-[#e2e2dc] rounded-lg ml-2"
        onClick={updateFilter}
      >
        <TbFilter className="mr-1" />
        {filterChecked ? "new -> old" : "old -> new"}
      </button>

      {isLoading && catchesData.length === 0 ? (
        <div className="my-2 h-72 rounded-lg bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_200%] animate-gradient-move"></div>
      ) : (
        <>
          <div className="my-2 grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] grid-rows-[repeat(auto-fit,1fr)] gap-x-2 gap-y-8">
            {catchesData.length !== 0
              ? catchesData.map((catchData, i) => (
                  <div
                    key={i}
                    className="border flex flex-col items-center border-black overflow-hidden rounded-lg py-2 shadow-lg max-w-[275px]"
                  >
                    <h3>Fish ID: {catchData.catchId}</h3>
                    <img
                      className="w-52 h-[17.7rem] my-1 object-cover object-top rounded-lg shadow-md"
                      src={catchData.imageUrl}
                      alt="Fish"
                      loading="lazy"
                    />
                    <p className="font-medium">{catchData.name}</p>
                    <p>{catchData.catchDate}</p>
                  </div>
                ))
              : "No data available."}
          </div>

          {/* End of infinite scroll */}
          {!isLoading &&
            catchesData.length > 0 &&
            currentPage === totalPages && (
              <label className="block text-center mt-4 opacity-20">
                &mdash; End &mdash;
              </label>
            )}
        </>
      )}

      {/* Loading spinner */}
      {catchesData.length > 0 && isLoading && (
        <div className="my-10 mx-auto w-8 h-8 border-4 border-[var(--secondary-colour)] border-t-transparent rounded-full animate-spin"></div>
      )}
    </>
  );
};

export default FishDisplay;
